import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../components/ui/form";
import { Button } from "../components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from "../components/ui/command";
import { z } from "zod";
import { useToast } from "../components/ui/use-toast";
import { ClipLoader } from "react-spinners";
import { cn } from "../lib/utils";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "../components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { useSwitchContextTradingPartner } from "../migration-api/api-hooks/trading-partners";
import { useGetAllClientsForTP } from "../migration-api/api-hooks/trading-partners";
import { ScrollArea } from "../components/ui/scroll-area";
import { Card } from "../components/ui/card";
import { useDispatch } from "react-redux";
import { setAuth } from "../store/features/authSlice";
import { useNavigate } from "react-router-dom";

const TradingPartnerClientSelector = ({ user }) => {
	const { toast } = useToast();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const formSchema = z.object({
		clientId: z.string().min(1, {
			message: "Please select a client",
		}),
	});

	const form = useForm({
		resolver: zodResolver(formSchema),
		defaultValues: {
			clientId: "",
		},
	});

	// Fetch all clients belonging to a trading partner
	const { data: clientsResponse, isLoading: isLoadingClients } =
		useGetAllClientsForTP();

	const clients = clientsResponse?.data || [];

	// Mutation for switching context
	const { mutate: switchContext, isPending } = useSwitchContextTradingPartner({
		onSuccess: (data) => {
			toast({
				variant: "success",
				title: "Success",
				description: "Context switched successfully",
			});

			// Update auth context with new data
			dispatch(
				setAuth({
					accessToken: data?.data?.tokens?.accessToken || data?.data?.accessToken,
					refreshToken: data?.data?.tokens?.refreshToken || data?.data?.refreshToken,
					user: data?.data?.user || data?.data,
					providerToken: data?.data?.providerToken,
					login: true,
				})
			);

			// Navigate to dashboard
			navigate("/pages", { replace: true });
		},
		onError: (error) => {
			toast({
				variant: "destructive",
				title: "Error",
				description:
					error?.response?.data?.message ||
					"Failed to switch context. Please try again.",
			});
		},
	});

	function onSubmit(data) {
		switchContext({ clientId: parseInt(data.clientId) });
	}

	return (
		<div className="w-full h-full">
			<div className="pl-24 pt-10">
				<img src="/sproxillogo.png" alt="" className="w-[120px]" />
			</div>
			<div className="flex flex-col">
				<div className="h-[80vh] flex flex-col gap-[10px] justify-center items-center">
					<Card className="h-[fit-content] w-[400px] p-[30px] min-w-[230px]">
						<h1 className="font-extrabold text-xl">Select Client</h1>
						<h4 className="text-xs text-gray-600 mb-6">
							Welcome {user?.firstName} {user?.lastName}! Please select the
							client you want to work with.
						</h4>

						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(onSubmit)}
								className="space-y-8"
							>
								{/* Client Selection */}
								<FormField
									control={form.control}
									name="clientId"
									render={({ field }) => (
										<FormItem className="flex flex-col">
											<FormLabel>Select Client</FormLabel>
											<Popover>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant="outline"
															role="combobox"
															className={cn(
																"w-[100%] justify-between",
																!field.value &&
																	"text-muted-foreground",
															)}
															disabled={isLoadingClients}
														>
															{isLoadingClients
																? "Loading clients..."
																: field.value
																	? clients.find(
																			(c) =>
																				c.id.toString() ===
																				field.value,
																		)?.clientName
																	: "Select client"}
															<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												<PopoverContent className="w-[340px] p-0">
													<Command>
														<CommandInput placeholder="Search client..." />
														<CommandEmpty>No client found.</CommandEmpty>
														<ScrollArea className="max-h-[300px]">
															<CommandGroup>
																{clients.map((client) => (
																	<CommandItem
																		value={client.clientName}
																		key={client.id}
																		onSelect={() => {
																			form.setValue(
																				"clientId",
																				client.id.toString(),
																			);
																		}}
																	>
																		<Check
																			className={cn(
																				"mr-2 h-4 w-4",
																				client.id.toString() ===
																					field.value
																					? "opacity-100"
																					: "opacity-0",
																			)}
																		/>
																		{client.clientName}
																	</CommandItem>
																))}
															</CommandGroup>
														</ScrollArea>
													</Command>
												</PopoverContent>
											</Popover>
											<FormMessage />
										</FormItem>
									)}
								/>

								<Button
									type="submit"
									className="w-full"
									disabled={isPending || isLoadingClients}
								>
									{isPending ? (
										<span className="mx-[20px]">
											<ClipLoader color="white" size={20} />
										</span>
									) : (
										<span>Continue</span>
									)}
								</Button>
							</form>
						</Form>
					</Card>
					<div className="text-primary text-[9px]">
						Copyright &copy; {new Date().getFullYear()}
					</div>
				</div>
			</div>
		</div>
	);
};

export default TradingPartnerClientSelector;